'use client';

import React, { useState, useMemo, useEffect } from 'react';
import useAuthStore from '@/store/auth';
import useMapsStore from '@/store/maps';
import { useMutationEditSettings } from '@/api/queries/customers';
import toast from 'react-hot-toast';
import {
  Sun,
  Moon,
  Map,
  Clock,
  Compass,
  Globe,
  Ship,
  MousePointer,
  Loader2,
  Check,
  Zap,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import CoordinateUtils from '@/utils/coordinate';
import { isWebGLSupported, isLowEndDevice } from '@/utils/hardwareDetection';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
] as const;

const MAP_MODE_OPTIONS = [
  {
    value: 'webgl',
    label: 'Web GL',
    badge: 'Smoother',
    desc: 'Hardware accelerated 3D & vector maps',
  },
  {
    value: 'leaflet',
    label: 'Leaflet',
    badge: 'Faster',
    desc: 'Lightweight raster maps for low bandwidth',
  },
] as const;

const MAP_OPTIONS = [
  { value: 'street', label: 'Street' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'satellite', label: 'Satellite' },
  { value: 'grayscale', label: 'Grayscale' },
] as const;

const REFRESH_OPTIONS = [
  { value: '1', label: '1 Minute' },
  { value: '5', label: '5 Minutes' },
  { value: '10', label: '10 Minutes' },
  { value: '20', label: '20 Minutes' },
  { value: '30', label: '30 Minutes' },
  { value: '40', label: '40 Minutes' },
  { value: '50', label: '50 Minutes' },
  { value: '60', label: '60 Minutes' },
];

const COORD_OPTIONS = [
  { value: 'DD', label: 'Decimal Degrees (DD)' },
  { value: 'DMS', label: 'Degrees Minutes Seconds (DMS)' },
];

export default function AppearanceTab() {
  const user = useAuthStore((state) => state.user);
  const setLogin = useAuthStore((state) => state.setLogin);
  const { mutateAsync, isPending } = useMutationEditSettings();

  const settings = useMemo(() => {
    try {
      if (user?.settingsObj) return user.settingsObj;
      if (user?.settings) return JSON.parse(user.settings);
      return {};
    } catch {
      return {};
    }
  }, [user]);

  const timezoneOptions = useMemo(() => {
    try {
      return CoordinateUtils.generateTimezoneOptions();
    } catch {
      const options = [];
      for (let i = -12; i <= 14; i++) {
        const label = i >= 0 ? `GMT+${i}` : `GMT${i}`;
        options.push({ value: String(i >= 0 ? `+${i}` : i), label: `${label} (UTC${i >= 0 ? '+' : ''}${i}:00)` });
      }
      return options;
    }
  }, []);

  const isWindyMap = useMapsStore((state) => state.overlaysSettings?.windyMap);
  const [theme, setTheme] = useState<string>(settings.theme || 'light');
  const [mapMode, setMapMode] = useState<string>(() => {
    if (isWindyMap) return 'leaflet';
    if (settings.map_mode) {
      return settings.map_mode === 'leaflet' || settings.map_mode === 'maps-leaflet' ? 'leaflet' : 'webgl';
    }
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('map_mode');
      if (saved === 'maps-leaflet' || saved === 'leaflet') return 'leaflet';
      if (saved === 'maps-gl' || saved === 'webgl') return 'webgl';
    }
    return 'webgl';
  });
  const [mapType, setMapType] = useState<string>(settings.map_type || 'street');
  const [autoRefresh, setAutoRefresh] = useState<string>(settings.auto_refresh || '5');
  const [coordFormat, setCoordFormat] = useState<string>(settings.coordinate_format || 'DD');
  const [timezone, setTimezone] = useState<string>(settings.timezone || '+7');
  const [showPort, setShowPort] = useState<boolean>(Boolean(settings.show_port_by_system));
  const [clickMap, setClickMap] = useState<boolean>(Boolean(settings.click_map));
  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);
  const [deviceCapability, setDeviceCapability] = useState<{ webglSupported: boolean; isLowEnd: boolean }>({
    webglSupported: true,
    isLowEnd: false,
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const webgl = isWebGLSupported();
      const lowEnd = isLowEndDevice();
      setDeviceCapability({ webglSupported: webgl, isLowEnd: lowEnd });
      if (!webgl || isWindyMap) {
        setMapMode('leaflet');
      }
    }
  }, [isWindyMap]);

  useEffect(() => {
    if (settings) {
      if (settings.theme) setTheme(settings.theme);
      if (settings.map_type) setMapType(settings.map_type);
      if (settings.map_mode) {
        const preferred = settings.map_mode === 'leaflet' || settings.map_mode === 'maps-leaflet' ? 'leaflet' : 'webgl';
        if (isWindyMap) {
          setMapMode('leaflet');
        } else {
          setMapMode(preferred === 'webgl' && typeof window !== 'undefined' && !isWebGLSupported() ? 'leaflet' : preferred);
        }
      }
      if (settings.auto_refresh) setAutoRefresh(settings.auto_refresh);
      if (settings.coordinate_format) setCoordFormat(settings.coordinate_format);
      if (settings.timezone) setTimezone(settings.timezone);
      if (settings.show_port_by_system !== undefined) setShowPort(Boolean(settings.show_port_by_system));
      if (settings.click_map !== undefined) setClickMap(Boolean(settings.click_map));
    }
  }, [settings, isWindyMap]);

  const handleSave = async () => {
    try {
      const payload = {
        theme,
        map_type: mapType,
        map_mode: mapMode,
        auto_refresh: autoRefresh,
        coordinate_format: coordFormat,
        timezone,
        draggable_marker: false,
        click_map: clickMap,
        show_port_by_system: showPort,
      };

      const response = await mutateAsync({ params: payload });
      if (response) {
        if (typeof response.settings === 'string' && !response.settingsObj) {
          try {
            response.settingsObj = JSON.parse(response.settings);
          } catch {}
        }
        setLogin(response);
      }

      // Update mapMode in localStorage & authStore
      const effectiveMapMode = isWindyMap ? 'leaflet' : mapMode;
      const engineMode = effectiveMapMode === 'leaflet' ? 'maps-leaflet' : 'maps-gl';
      if (typeof window !== 'undefined') {
        localStorage.setItem('map_mode', engineMode);
      }
      useAuthStore.getState().setMapMode(engineMode, false);

      // Apply theme immediately to DOM
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      toast.success('Settings saved successfully!');
      setShowRefreshModal(true);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Could not save settings';
      toast.error(`Save failed: ${message}`);
    }
  };

  return (
    <div className="p-5 space-y-6">

      {/* Interface Theme */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500">
            <Sun className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Interface Theme</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Select your preferred color mode</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pl-10">
          {THEME_OPTIONS.map((t) => {
            const active = theme === t.value;
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTheme(t.value)}
                className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all text-left ${active
                  ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300'
                  }`}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`h-7 w-7 rounded-lg flex items-center justify-center ${t.value === 'dark'
                      ? 'bg-gray-800 text-amber-300'
                      : 'bg-amber-100 text-amber-600'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold capitalize">{t.label}</span>
                </div>
                {active && <Check className="w-4 h-4 text-blue-500" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Default Map Type */}
      <div className="space-y-2.5 border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-500">
            <Map className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Default Map Type</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Select map style for first-time access</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 pl-10">
          {MAP_OPTIONS.map((m) => {
            const active = mapType === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setMapType(m.value)}
                className={`py-2.5 px-3 rounded-xl border-2 text-xs font-semibold capitalize transition-all text-center ${active
                  ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300'
                  }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Map Mode (Engine) */}
      <div className="space-y-2.5 border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Map Engine (Map Mode)</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Web GL (smoother) or Leaflet (faster)</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 pl-10">
          {MAP_MODE_OPTIONS.map((m) => {
            const isWebgl = m.value === 'webgl';
            const isWeatherRestricted = isWebgl && isWindyMap;
            const isUnsupported = (isWebgl && !deviceCapability.webglSupported) || isWeatherRestricted;
            const active = mapMode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => {
                  if (isWeatherRestricted) {
                    toast.error('Weather Mode sedang aktif. Matikan Weather Maps terlebih dahulu jika ingin berganti ke WebGL.');
                    return;
                  }
                  if (isUnsupported) {
                    toast.error('Browser/HP tidak mendukung WebGL. Otomatis menggunakan Leaflet.');
                    return;
                  }
                  setMapMode(m.value);
                }}
                className={`flex flex-col justify-between p-3 rounded-xl border-2 transition-all text-left ${
                  isUnsupported
                    ? 'opacity-60 border-dashed border-slate-300 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/40 cursor-not-allowed'
                    : active
                    ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <div className="flex items-center justify-between w-full mb-1">
                  <span className="text-xs font-bold">{m.label}</span>
                  <span
                    className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                      isWeatherRestricted
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                        : isUnsupported
                        ? 'bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400'
                        : active
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {isWeatherRestricted ? 'WEATHER MODE' : isUnsupported ? 'UNSUPPORTED' : m.badge}
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
                  {isWeatherRestricted ? 'Wajib Leaflet saat Weather Mode aktif' : isUnsupported ? 'Tidak didukung browser/HP ini' : m.desc}
                </p>
                {m.value === 'leaflet' && (deviceCapability.isLowEnd || isWindyMap) && (
                  <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-semibold mt-1">
                    {isWindyMap ? '✓ Aktif untuk Weather Radar' : '✓ Rekomendasi HP ini'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {!deviceCapability.webglSupported && (
          <div className="ml-10 flex items-start gap-1.5 p-2 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40 text-[11px] text-rose-600 dark:text-rose-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Browser/HP ini tidak mendukung WebGL. Sistem otomatis menggunakan engine Leaflet.</span>
          </div>
        )}
      </div>

      {/* Auto Refresh */}
      <div className="space-y-2 border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-500">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Auto Refresh</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Interval for automatic data refresh</p>
          </div>
        </div>

        <div className="pl-10">
          <select
            value={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.value)}
            className="w-full px-4 py-3 rounded-xl text-xs font-medium border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          >
            {REFRESH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Coordinate Format */}
      <div className="space-y-2 border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-500">
            <Compass className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Coordinate Format</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Choose Decimal Degrees or DMS format</p>
          </div>
        </div>

        <div className="pl-10">
          <select
            value={coordFormat}
            onChange={(e) => setCoordFormat(e.target.value)}
            className="w-full px-4 py-3 rounded-xl text-xs font-medium border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          >
            {COORD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timezone */}
      <div className="space-y-2 border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-500">
            <Globe className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Timezone</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Choose your location timezone</p>
          </div>
        </div>

        <div className="pl-10">
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-4 py-3 rounded-xl text-xs font-medium border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          >
            {timezoneOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Show Port By System */}
      <div className="border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 pr-4">
            <div className="h-8 w-8 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center text-red-500 shrink-0">
              <Ship className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Show Port By System</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Display system ports on map load</p>
            </div>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={showPort}
            onClick={() => setShowPort(!showPort)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showPort ? 'bg-blue-600' : 'bg-slate-200 dark:bg-gray-700'
              }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showPort ? 'translate-x-5' : 'translate-x-0'
                }`}
            />
          </button>
        </div>
      </div>

      {/* Click Map */}
      <div className="border-t border-slate-100 dark:border-gray-800 pt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 pr-4">
            <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 shrink-0">
              <MousePointer className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-[#021c32] dark:text-white">Click Map</h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Tap on map to inspect coordinates</p>
            </div>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={clickMap}
            onClick={() => setClickMap(!clickMap)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${clickMap ? 'bg-blue-600' : 'bg-slate-200 dark:bg-gray-700'
              }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${clickMap ? 'translate-x-5' : 'translate-x-0'
                }`}
            />
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4 pb-28 border-t border-slate-100 dark:border-gray-800">
        <button
          type="button"
          onClick={handleSave}
          disabled={isPending}
          className="w-full py-3.5 px-4 rounded-xl font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>

      {/* Refresh Confirmation Modal */}
      {showRefreshModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[9999999] flex items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-2xl border border-slate-100 dark:border-gray-700 space-y-4 text-center">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto shadow-inner">
              <RefreshCw className="w-6 h-6 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#021c32] dark:text-white">Refresh Page?</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                Settings have been saved. Refresh the page to apply changes to the map engine and system preferences.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.location.reload();
                  }
                }}
                className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-all shadow-md shadow-blue-500/25 flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
